import { trpc } from "@/lib/trpc";
import { UNAUTHED_ERR_MSG } from '@shared/const';
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { httpBatchLink, TRPCClientError } from "@trpc/client";
import { createRoot } from "react-dom/client";
import superjson from "superjson";
import App from "./App";
import { izBaslat } from "@/lib/talep_kaynagi";
import { getLoginUrl } from "./const";
import "./index.css";

// Talep kaynağı izi (geliş kanalı + gezilen sayfalar) ilk sayfadan itibaren tutulur
izBaslat();

// Yeni bir sürüm yayına alındıktan sonra açık kalan eski sekmede tembel yüklenen bir parça
// bulunamazsa (sunucu artık 404 döner) sayfa bir kez yenilenip yeni sürüm yüklenir.
// Sayfa az önce (15 sn içinde) bir yenilemeyle açıldıysa tekrar yenilenmez (döngü olmaz; depolama kullanılmaz).
window.addEventListener("vite:preloadError", event => {
  let tur = "navigate";
  try {
    tur = (performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined)?.type ?? "navigate";
  } catch {
    // eski tarayıcı: yine bir kez yenilenir
  }
  if (tur === "reload" && performance.now() < 15_000) return;
  event.preventDefault();
  window.location.reload();
});

const queryClient = new QueryClient();

const redirectToLoginIfUnauthorized = (error: unknown) => {
  if (!(error instanceof TRPCClientError)) return;
  if (typeof window === "undefined") return;

  const isUnauthorized = error.message === UNAUTHED_ERR_MSG;

  if (!isUnauthorized) return;

  // OAuth ortamı tanımlı değilse giriş adresi yoktur; yönlendirme yapılmaz, sayfa kendi "giriş gerekli" durumunu gösterir.
  const girisAdresi = getLoginUrl();
  if (girisAdresi) window.location.href = girisAdresi;
};

queryClient.getQueryCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.query.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Query Error]", error);
  }
});

queryClient.getMutationCache().subscribe(event => {
  if (event.type === "updated" && event.action.type === "error") {
    const error = event.mutation.state.error;
    redirectToLoginIfUnauthorized(error);
    console.error("[API Mutation Error]", error);
  }
});

const trpcClient = trpc.createClient({
  links: [
    httpBatchLink({
      url: "/api/trpc",
      transformer: superjson,
      fetch(input, init) {
        return globalThis.fetch(input, {
          ...(init ?? {}),
          credentials: "include",
        });
      },
    }),
  ],
});

// Uretimde stil dosyasi engelsiz (preload) yuklenir; stilsiz ilk boyama (FOUC) ve
// buna bagli CLS olmasin diye React, CSS hazir olana kadar (en fazla 2 sn) bekler.
function stilHazir(): Promise<void> {
  return new Promise(resolve => {
    const link = document.querySelector<HTMLLinkElement>("link[data-dj-css]");
    if (!link) return resolve();
    const baslangic = Date.now();
    const uygulandi = () => {
      try {
        return link.rel === "stylesheet" && !!link.sheet && link.sheet.cssRules.length > 0;
      } catch { return link.rel === "stylesheet" && !!link.sheet; }
    };
    const kontrol = () => {
      if (uygulandi() || Date.now() - baslangic > 2000) return resolve();
      requestAnimationFrame(kontrol);
    };
    kontrol();
  });
}

stilHazir().then(() => {
  createRoot(document.getElementById("root")!).render(
    <trpc.Provider client={trpcClient} queryClient={queryClient}>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </trpc.Provider>
  );
});
